import React from 'react';
import { Badge, Divider, Group, Modal, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconMathFunction } from '@tabler/icons-react';
import type { IDesgloseCostoEquipo } from '../../../modelos/CotizadorServicios/entidades/IResultadoCotizacionServicios';
import { formatoMoneda } from '../interfaces';

interface ModalDesgloseEquipoVistaProps {
    equipo: IDesgloseCostoEquipo | null;
    alCerrar: () => void;
}

type TipoFila = 'base' | 'suma' | 'resta' | 'subtotal';

const Fila: React.FC<{ etiqueta: string; valor?: number; tipo?: TipoFila }> = ({ etiqueta, valor, tipo = 'base' }) => {
    if (valor === undefined) return null;
    const color = tipo === 'resta' ? 'red' : tipo === 'suma' ? 'blue' : undefined;
    const signo = tipo === 'resta' ? '- ' : tipo === 'suma' ? '+ ' : tipo === 'subtotal' ? '= ' : '';
    return (
        <Group justify="space-between">
            <Text size="sm" fw={tipo === 'subtotal' ? 700 : undefined} c={tipo === 'suma' || tipo === 'resta' ? 'dimmed' : undefined} pl={tipo === 'suma' || tipo === 'resta' ? 'md' : undefined}>
                {signo}{etiqueta}
            </Text>
            <Text size="sm" fw={tipo === 'subtotal' ? 700 : 500} c={color}>{formatoMoneda(valor)}</Text>
        </Group>
    );
};

/** Desglose paso a paso del precio de un equipo (uso interno). */
export const ModalDesgloseEquipoVista: React.FC<ModalDesgloseEquipoVistaProps> = ({ equipo, alCerrar }) => (
    <Modal
        opened={!!equipo}
        onClose={alCerrar}
        size="lg"
        title={
            <Group>
                <ThemeIcon variant="light" size="lg" radius="xl" color="primary"><IconMathFunction size={20} /></ThemeIcon>
                <Text fw={700} tt="uppercase">Desglose del Equipo</Text>
            </Group>
        }
    >
        {equipo && (
            <Stack gap="xs">
                <Group justify="space-between">
                    <Text size="sm" c="dimmed">Equipo</Text>
                    <Text fw={700}>{equipo.Referencia} ({equipo.EsAscensor ? 'Ascensor' : 'Escalera'}) × {equipo.Cantidad}</Text>
                </Group>
                <Divider variant="dashed" />
                <Fila etiqueta="Costo base" valor={equipo.CostoBase} />
                <Fila etiqueta="Paradas" valor={equipo.CostoParadas} tipo="suma" />
                <Fila etiqueta="Velocidad" valor={equipo.CostoVelocidad} tipo="suma" />
                <Fila etiqueta="Tipo de uso" valor={equipo.CostoUso} tipo="suma" />
                <Fila etiqueta="Horario" valor={equipo.CostoHorario} tipo="suma" />
                <Fila etiqueta="Costo base total" valor={equipo.TotalCostoBase} tipo="subtotal" />
                <Fila etiqueta="CIF" valor={equipo.CostoCif} tipo="suma" />
                <Fila etiqueta="Costos especiales" valor={equipo.CostosEspeciales} tipo="suma" />
                <Fila etiqueta="Subtotal" valor={equipo.Subtotal} tipo="subtotal" />
                <Fila etiqueta={`Incremento anual (${equipo.PorcentajeIncrementoAnual}%)`} valor={equipo.SubtotalConIncremento - equipo.Subtotal} tipo="suma" />
                <Fila etiqueta="Subtotal con incremento anual" valor={equipo.SubtotalConIncremento} tipo="subtotal" />
                <Divider variant="dashed" />
                <Fila etiqueta="Precio unitario (con margen)" valor={equipo.PrecioUnitario} />
                <Fila etiqueta="Descuento" valor={equipo.Descuento} tipo="resta" />
                <Fila etiqueta="Incremento" valor={equipo.Incremento} tipo="suma" />
                <Divider />
                <Group justify="space-between">
                    <Text fw={800} tt="uppercase">Precio final unitario</Text>
                    <Badge size="xl" variant="filled" color="primary" style={{ textTransform: 'none' }}>{formatoMoneda(equipo.PrecioFinalUnitario)}</Badge>
                </Group>
                <Group justify="space-between">
                    <Text size="sm" c="dimmed">Precio total ({equipo.Cantidad} {equipo.Cantidad === 1 ? 'equipo' : 'equipos'})</Text>
                    <Text fw={700}>{formatoMoneda(equipo.PrecioTotal)}</Text>
                </Group>
            </Stack>
        )}
    </Modal>
);
